import Parchment from 'parchment';
import Quill from 'quill';
import {Subscription} from 'rxjs';
import {RichTextComponent} from './rich-text.component';
import {catchError, first, timeout} from 'rxjs/operators';

const EmbedBlot: typeof Parchment.Embed = Quill.import('blots/embed');

export const instances = new Map<Quill, RichTextComponent>();

export class VariableBlot extends EmbedBlot {
  public static blotName = 'variable';
  public static tagName = 'MARK';
  public static className = 'ql-variable';

  private subs = new Subscription();
  private label: HTMLSpanElement;
  private isAttached = false;

  private richText: RichTextComponent;
  private quill: Quill;

  constructor(domNode: HTMLElement, public name: string) {
    super(domNode);

    this.label = domNode.querySelector('span');

    domNode.addEventListener('click', this.click);
  }

  public static create(name: string): HTMLElement {
    const domNode = super.create(name) as HTMLElement;

    domNode.setAttribute('title', name);
    domNode.appendChild(document.createTextNode(name));

    return domNode;
  }

  public static value(node: HTMLElement): string {
    return node.getAttribute('title');
  }

  public attach(): void {
    super.attach();

    this.quill = this.getQuill();
    this.richText = instances.get(this.quill);

    if (!this.richText || this.isAttached) {
      return;
    }

    this.isAttached = true;

    this.subs.add(
      this.richText?.calculatedTokenValueStore?.tokenValue(this.name)?.subscribe(value => {
        this.tokenUpdate(this.name, value);
      })
    );

    if (this.richText?.blocksConfig?.tokensEditable) {
      this.subs.add(
        this.richText?.calculatedTokenValueStore?.tokenValue(this.name)?.pipe(
          first(),
          timeout(100),
          catchError(() => {
            // no value within 100ms
            this.label.setAttribute('class', 'variable missing variable-' + this.name);
            return [];
          })
        ).subscribe()
      );
    }

    this.richText?.tokenStore?.updateTokenInstancesDebounced();
  }

  public detach(): void {
    super.detach();

    this.subs.unsubscribe();
    this.isAttached = false;

    this.richText?.tokenStore?.updateTokenInstancesDebounced();
  }

  private tokenUpdate(name: string, value: any) {
    const label = this.label;
    const showName = value === null || value === undefined || String(value).length === 0;

    label.textContent = showName ? name : value;

    if (this.richText.blocksConfig?.tokensEditable) {
      label.setAttribute('class', showName ? 'variable no-value' : 'variable has-value');
      label.setAttribute('data-tooltip-text', name);
      label.setAttribute('data-tooltip-position', 'bottom');
    } else if (this.richText.blocksConfig?.printOptimized && showName) {
      label.setAttribute('class', 'variable-placeholder');
    }
  }

  private click = () => {
    const index = this.quill.getIndex(this);

    this.quill.setSelection(index, 1);
    this.richText.tokenScrollService?.scrollToTokenInput?.next(this.name);
  };

  private getQuill(): any {
    if (!this.scroll || !this.scroll.domNode.parentNode) {
      return null;
    }

    return Quill.find(this.scroll.domNode.parentNode);
  }
}
